import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import { 
  Add as AddIcon,
  Store as StoreIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAllAssignments } from '../../../services/assignmentService';
import { fetchHierarchyDefinitions, fetchOrgUnitsWithLevels, getHierarchyLabel } from '../../../services/hierarchyService';
import { usePeopleAdmin } from '../../../context/PeopleAdminContext';
import { useUser } from 'components/RDAuthContext';
import DeactivateAssignmentModal from '../DeactivateAssignmentModal';
import AddAssignmentModal from '../AddAssignmentModal';
import { API, graphqlOperation } from 'aws-amplify';
import { updateLocationEmployee, updateOrgUnitEmployee, createLocationEmployee, createOrgUnitEmployee } from 'graphql/mutations';
import { locationsByClientSorted } from 'graphql/queries';
import * as logger from 'utils/logger';

/**
 * Assignments tab for employee details - displays all employee assignments in card layout
 */
function EmployeeAssignmentsTab({ 
  employee, 
  loading = false 
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  
  const { clientId } = usePeopleAdmin();
  const { user: currentUser } = useUser();
  const queryClient = useQueryClient();

  // Fetch real assignments data from the API
  const { 
    data: assignments = [], 
    isLoading: isLoadingAssignments,
    error: assignmentsError 
  } = useQuery({
    queryKey: ['employeeAssignments', employee?.id, clientId],
    queryFn: () => fetchAllAssignments(employee.id, clientId),
    enabled: !!employee?.id && !!clientId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });

  // Fetch hierarchy definitions
  const { data: hierarchyDefinitions = [] } = useQuery({
    queryKey: ['hierarchyDefinitions', clientId],
    queryFn: () => fetchHierarchyDefinitions(clientId),
    enabled: !!clientId,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000
  });

  // Fetch org units with their levels
  const { data: orgUnitsMap = {} } = useQuery({
    queryKey: ['orgUnitsWithLevels', clientId],
    queryFn: () => fetchOrgUnitsWithLevels(clientId),
    enabled: !!clientId,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000
  });

  // Fetch available locations for the add modal
  const { data: availableLocations = [] } = useQuery({
    queryKey: ['availableLocations', clientId],
    queryFn: async () => {
      const result = await API.graphql(
        graphqlOperation(locationsByClientSorted, {
          clientId: clientId,
          sortDirection: 'ASC',
          limit: 500
        })
      );
      return result.data.locationsByClientSorted?.items || [];
    },
    enabled: !!clientId && addModalOpen,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000
  });

  // Mutation for adding assignments
  const addAssignmentMutation = useMutation({
    mutationFn: async (data) => {
      const now = new Date().toISOString();

      if (data.type === 'location') {
        const result = await API.graphql(
          graphqlOperation(createLocationEmployee, {
            input: {
              clientId: data.clientId,
              employeeId: data.employeeId,
              locationId: data.locationId,
              roleDefinitionId: data.roleDefinitionId,
              isPrimary: data.isPrimary || false,
              isActive: data.isActive,
              effectiveDate: data.effectiveDate,
              assignedBy: currentUser?.id || currentUser?.userName,
              assignmentReason: data.assignmentReason,
              employeeNameSnapshot: data.employeeNameSnapshot,
              locationNameSnapshot: data.locationNameSnapshot,
              roleSnapshot: data.roleSnapshot,
              createdAt: now,
              lastModified: now
            }
          })
        );
        return result.data.createLocationEmployee;
      } else {
        // For org unit assignments - keeping existing logic for now
        const result = await API.graphql(
          graphqlOperation(createOrgUnitEmployee, {
            input: {
              clientId: data.clientId,
              employeeId: data.employeeId,
              orgUnitId: data.orgUnitId,
              managementLevel: data.orgLevel,
              roleDefinitionId: data.roleDefinitionId,
              isActive: data.isActive,
              effectiveDate: data.effectiveDate,
              assignedBy: currentUser?.id || currentUser?.userName,
              assignmentReason: data.assignmentReason,
              employeeNameSnapshot: data.employeeNameSnapshot,
              orgUnitNameSnapshot: data.orgUnitName,
              roleSnapshot: data.roleSnapshot,
              createdAt: now,
              lastModified: now
            }
          })
        );
        return result.data.createOrgUnitEmployee;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['employeeAssignments', employee?.id, clientId]);
      queryClient.invalidateQueries(['employees', clientId]); // Refresh employee list to update counts
      logger.info('Assignment added successfully');
      setAddModalOpen(false);
    },
    onError: (error) => {
      logger.error('Failed to add assignment', error);
    }
  });

  // Mutation for deactivating assignments
  const deactivateAssignmentMutation = useMutation({
    mutationFn: async (data) => {
      if (data.assignmentType === 'location') {
        const result = await API.graphql(
          graphqlOperation(updateLocationEmployee, {
            input: {
              id: data.assignmentId,
              endDate: data.endDate,
              changeReason: data.changeReason,
              changedBy: data.changedBy,
              isActive: false
            }
          })
        );
        return result.data.updateLocationEmployee;
      } else {
        const result = await API.graphql(
          graphqlOperation(updateOrgUnitEmployee, {
            input: {
              id: data.assignmentId,
              endDate: data.endDate,
              changeReason: data.changeReason,
              changedBy: data.changedBy,
              isActive: false
            }
          })
        );
        return result.data.updateOrgUnitEmployee;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['employeeAssignments', employee?.id, clientId]);
      queryClient.invalidateQueries(['employees', clientId]); // Refresh employee list to update counts
      logger.info('Assignment deactivated successfully');
    },
    onError: (error) => {
      logger.error('Failed to deactivate assignment', error);
    }
  });

  // Filter assignments by active status and sort by start date
  const filteredAndSortedAssignments = useMemo(() => {
    let filtered = [...assignments];
    
    // Filter by active status
    if (!showInactive) {
      filtered = filtered.filter(a => a.isActive);
    }
    
    // Sort by start date (most recent first)
    filtered.sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
      const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
      return dateB - dateA;
    });
    
    return filtered;
  }, [assignments, showInactive]);

  // Group ALL assignments for totals (unfiltered)
  const allGroupedAssignments = useMemo(() => {
    const groups = {
      location: [],
      byOrgLevel: {}
    };

    assignments.forEach(assignment => {
      if (assignment.type === 'location') {
        groups.location.push(assignment);
      } else if (assignment.type === 'orgUnit') {
        let orgLevel = assignment.orgLevel;
        
        if (assignment.orgUnitId && orgUnitsMap[assignment.orgUnitId]) {
          orgLevel = orgUnitsMap[assignment.orgUnitId].orgLevel || orgLevel;
        }
        
        if (!orgLevel) {
          orgLevel = 'ORGLEVEL_1';
        }
        
        if (!groups.byOrgLevel[orgLevel]) {
          groups.byOrgLevel[orgLevel] = [];
        }
        
        groups.byOrgLevel[orgLevel].push({
          ...assignment,
          orgLevel
        });
      }
    });

    return groups;
  }, [assignments, orgUnitsMap]);

  // Group visible assignments by type and org level (filtered)
  const groupedAssignments = useMemo(() => {
    const groups = {
      location: [],
      byOrgLevel: {}
    };

    filteredAndSortedAssignments.forEach(assignment => {
      if (assignment.type === 'location') {
        groups.location.push(assignment);
      } else if (assignment.type === 'orgUnit') {
        let orgLevel = assignment.orgLevel;
        
        if (assignment.orgUnitId && orgUnitsMap[assignment.orgUnitId]) {
          orgLevel = orgUnitsMap[assignment.orgUnitId].orgLevel || orgLevel;
        }
        
        if (!orgLevel) {
          orgLevel = 'ORGLEVEL_1';
        }
        
        if (!groups.byOrgLevel[orgLevel]) {
          groups.byOrgLevel[orgLevel] = [];
        }
        
        groups.byOrgLevel[orgLevel].push({
          ...assignment,
          orgLevel
        });
      }
    });

    return groups;
  }, [filteredAndSortedAssignments, orgUnitsMap]);

  // Filter assignments based on search
  const filterAssignments = (assignmentList) => {
    if (!searchTerm) return assignmentList;
    
    const search = searchTerm.toLowerCase();
    return assignmentList.filter(assignment => {
      const locationOrOrgName = assignment.type === 'location' ? 
        assignment.locationName : assignment.orgUnitName;
      return (
        locationOrOrgName?.toLowerCase().includes(search) ||
        assignment.roleName?.toLowerCase().includes(search) ||
        assignment.department?.toLowerCase().includes(search)
      );
    });
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'MM/dd/yyyy');
    } catch {
      return dateString;
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(parseISO(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  // Handle deactivation
  const handleDeactivateClick = (assignment) => {
    setSelectedAssignment(assignment);
    setDeactivateModalOpen(true);
  };

  const handleDeactivate = async (data) => {
    await deactivateAssignmentMutation.mutateAsync(data);
    setDeactivateModalOpen(false);
    setSelectedAssignment(null);
  };

  // Render assignment card
  const renderAssignmentCard = (assignment) => (
    <ListItem 
      key={assignment.id}
      sx={{ 
        px: 0,
        py: 1.5,
        alignItems: 'flex-start',
        '&:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'divider'
        }
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32, 
            bgcolor: assignment.isActive ? 'primary.main' : 'grey.400'
          }}
        >
          {assignment.type === 'location' ? (
            <StoreIcon sx={{ fontSize: 18 }} />
          ) : (
            <BusinessIcon sx={{ fontSize: 18 }} />
          )}
        </Avatar>
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" fontWeight="600">
              {assignment.type === 'location' ? assignment.locationName : assignment.orgUnitName}
            </Typography>
            {assignment.isPrimary && (
              <Chip 
                label="Primary" 
                size="small" 
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {!assignment.isActive && (
              <Chip 
                label="Inactive" 
                size="small" 
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
              <strong>Role:</strong> {assignment.roleName || 'N/A'}
              {assignment.type === 'location' && assignment.weeklyHours && (
                <> • <strong>Hours:</strong> {assignment.weeklyHours}/week</>
              )}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {formatDate(assignment.startDate)} - {assignment.endDate ? formatDate(assignment.endDate) : 'Present'}
              </Typography>
            </Box>
            
            {assignment.assignedBy && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Assigned by: {assignment.assignedBy}
                </Typography>
              </Box>
            )}
            
            {assignment.assignmentReason && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.25 }} />
                <Typography variant="caption" color="text.secondary">
                  Reason: {assignment.assignmentReason}
                </Typography>
              </Box>
            )}
            
            {!assignment.isActive && assignment.changeReason && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 0.5, 
                mt: 0.5,
                p: 0.75,
                bgcolor: 'grey.100',
                borderLeft: '3px solid',
                borderColor: 'grey.400',
                borderRadius: 0.5
              }}>
                <BlockIcon sx={{ fontSize: 14, color: 'grey.600', mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
                    Deactivation Reason: {assignment.changeReason}
                  </Typography>
                  {assignment.changedBy && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Changed by: {assignment.changedBy}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            
            {assignment.type === 'location' && assignment.shift && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {assignment.shift}
                </Typography>
              </Box>
            )}
          </Box>
        }
      />
      {assignment.isActive && (
        <Box>
          <Tooltip title="Deactivate Assignment">
            <IconButton 
              size="small" 
              color="error"
              onClick={() => handleDeactivateClick(assignment)}
              disabled={loading || deactivateAssignmentMutation.isPending}
              sx={{ padding: '4px' }}
            >
              <BlockIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </ListItem>
  );

  // Render assignment section
  const renderAssignmentSection = (title, assignmentList, totalList, icon, key) => {
    const filteredList = filterAssignments(assignmentList);
    
    if (totalList.length === 0 && !searchTerm) {
      return null;
    }

    return (
      <Grid item xs={12} key={key || title}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {icon}
              <Typography variant="subtitle2" fontWeight="600">
                {title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`${totalList.length} Total`}
                size="small"
                variant="outlined"
              />
              {totalList.filter(a => a.isActive).length > 0 && (
                <Chip
                  label={`${totalList.filter(a => a.isActive).length} Active`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
              {totalList.filter(a => !a.isActive).length > 0 && (
                <Chip
                  label={`${totalList.filter(a => !a.isActive).length} Inactive`}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          
          {filteredList.length > 0 ? (
            <List dense sx={{ pt: 0 }}>
              {filteredList.map(renderAssignmentCard)}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {searchTerm 
                ? 'No assignments found matching your search'
                : 'No assignments in this category'}
            </Typography>
          )}
        </Paper>
      </Grid>
    );
  };

  // Handle loading state
  if (isLoadingAssignments) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[...Array(3)].map((_, i) => (
            <Grid item xs={12} key={i}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Skeleton variant="text" width="30%" height={30} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={120} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Handle error state
  if (assignmentsError) {
    return (
      <Alert severity="error">
        Failed to load assignments. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with Search, Filter and Add Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            placeholder="Search assignments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                color="primary"
              />
            }
            label="Show Inactive"
          />
        </Box>
        <AdminButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          size="small"
        >
          Add Assignment
        </AdminButton>
      </Box>

      {/* Assignment Sections */}
      <Grid container spacing={2}>
        {/* Location Assignments */}
        {renderAssignmentSection(
          'Location Assignments',
          groupedAssignments.location,
          allGroupedAssignments.location,
          <LocationIcon color="primary" />
        )}

        {/* Org Unit Assignments - Each level in its own box */}
        {Object.keys(allGroupedAssignments.byOrgLevel)
          .sort((a, b) => {
            const levelA = parseInt(a.replace('ORGLEVEL_', '') || '99');
            const levelB = parseInt(b.replace('ORGLEVEL_', '') || '99');
            return levelA - levelB;
          })
          .map(orgLevel => {
            const levelAssignments = groupedAssignments.byOrgLevel[orgLevel] || [];
            const totalLevelAssignments = allGroupedAssignments.byOrgLevel[orgLevel] || [];
            const hierarchyLabel = getHierarchyLabel(orgLevel, hierarchyDefinitions);
            
            const iconColors = {
              'ORGLEVEL_1': 'secondary',
              'ORGLEVEL_2': 'info.main',
              'ORGLEVEL_3': 'warning.main',
              'ORGLEVEL_4': 'success.main',
              'ORGLEVEL_5': 'error.main'
            };
            const iconColor = iconColors[orgLevel] || 'action';
            
            return renderAssignmentSection(
              `${hierarchyLabel} Assignments`,
              levelAssignments,
              totalLevelAssignments,
              <BusinessIcon sx={{ color: iconColor }} />,
              orgLevel
            );
          })}
      </Grid>

      {/* No Assignments */}
      {filteredAndSortedAssignments.length === 0 && !searchTerm && (
        <Alert 
          severity="info" 
          sx={{ 
            mt: 2,
            '& .MuiAlert-message': {
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }
          }}
        >
          {showInactive 
            ? 'No assignments found for this employee.'
            : 'No active assignments found for this employee. Toggle "Show Inactive" to see historical assignments.'}
        </Alert>
      )}

      {/* No search results */}
      {searchTerm && filteredAndSortedAssignments.length > 0 && 
       Object.values(groupedAssignments).every(group => 
         Array.isArray(group) ? filterAssignments(group).length === 0 : 
         Object.values(group).every(subGroup => filterAssignments(subGroup).length === 0)
       ) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No assignments found matching "{searchTerm}". Try adjusting your search criteria.
        </Alert>
      )}

      {/* Deactivation Modal */}
      <DeactivateAssignmentModal
        open={deactivateModalOpen}
        onClose={() => {
          setDeactivateModalOpen(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
        onDeactivate={handleDeactivate}
        currentUser={currentUser}
      />

      {/* Add Assignment Modal */}
      <AddAssignmentModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        employee={employee}
        onAdd={(data) => addAssignmentMutation.mutate(data)}
        locations={availableLocations.filter(loc => loc.isActive !== false)}
        orgUnits={Object.values(orgUnitsMap).filter(unit => unit.id && unit.isActive !== false)}
        hierarchyDefinitions={hierarchyDefinitions}
        loading={addAssignmentMutation.isPending}
        clientId={clientId}
      />
    </Box>
  );
}

EmployeeAssignmentsTab.propTypes = {
  employee: PropTypes.object.isRequired,
  loading: PropTypes.bool
};

export default EmployeeAssignmentsTab;